import React, { useState } from "react";
import { TextField, Button, IconButton, InputAdornment, Alert, LinearProgress, Box, Typography } from "@mui/material";
import { Visibility, VisibilityOff, Lock, Email } from "@mui/icons-material";
import { useSearchParams, useNavigate } from "react-router-dom";
import useAxios from "../hooks/useAxios";
import { PASSWORD_REG_EXPRESSION, PASSWORD_REQUIREMENTS_MESSAGE } from "../utils/constant";
import '../styles/Login.css';

export function SetPassword() {
    const [searchParams] = useSearchParams();
    const email = searchParams.get("email") || "";
    const token = searchParams.get("token") || "";
    const navigate = useNavigate();

    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [error, setError] = useState({ password: "", confirmPassword: "" });
    const [globalMessage, setGlobalMessage] = useState("");
    const [isLoading, setIsLoading] = useState(false);

    const { postWithAxios } = useAxios();

    /**
     * Calcule la force du mot de passe (0-100)
     * Chaque critère validé = +20%
     */
    const getPasswordStrength = (pwd: string): number => {
        if (!pwd) return 0;

        let strength = 0;

        // Longueur minimum (8 chars)
        if (pwd.length >= 8) strength += 20;

        // Au moins une minuscule
        if (/[a-z]/.test(pwd)) strength += 20;

        // Au moins une majuscule
        if (/[A-Z]/.test(pwd)) strength += 20;

        // Au moins un chiffre
        if (/\d/.test(pwd)) strength += 20;

        // Au moins un caractère spécial
        if (/[!@#$%^&*(),.?":{}|<>]/.test(pwd)) strength += 20;

        return strength;
    };

    /**
     * Retourne la couleur selon la force
     */
    const getPasswordStrengthColor = (strength: number): "error" | "warning" | "info" | "success" => {
        if (strength < 40) return "error";
        if (strength < 60) return "warning";
        if (strength < 100) return "info";
        return "success";
    };

    /**
     * Retourne le label selon la force
     */
    const getPasswordStrengthLabel = (strength: number): string => {
        if (strength === 0) return "";
        if (strength < 40) return "Faible";
        if (strength < 60) return "Moyen";
        if (strength < 100) return "Bon";
        return "Excellent";
    };

    const passwordStrength = getPasswordStrength(password);

    const validateForm = () => {
        const newError = {
            password: !PASSWORD_REG_EXPRESSION.test(password)
                ? PASSWORD_REQUIREMENTS_MESSAGE
                : "",
            confirmPassword: password !== confirmPassword ? "Les mots de passe ne correspondent pas" : "",
        };
        setError(newError);
        return !newError.password && !newError.confirmPassword;
    };


    const handleSubmit = async () => {
        if (!validateForm()) return;
        setIsLoading(true);
        setGlobalMessage("");
        try {
            await postWithAxios('/auth/reset-password', {
                email,
                token,
                password: password
            });
            setGlobalMessage("Mot de passe modifié avec succès !");
            setTimeout(() => navigate("/"), 2000);
        } catch (err: any) {
            setGlobalMessage("Erreur lors de la mise à jour du mot de passe. Veuillez réessayer.");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="login-container">
            <div className="login-card">
                <div className="login-header">
                    <h1 className="login-title">Réinitialisation du mot de passe</h1>
                    <p className="login-subtitle">
                        Entrez votre nouveau mot de passe pour votre compte {email}.
                    </p>
                </div>

                {globalMessage && <Alert severity="info" className="error-alert">{globalMessage}</Alert>}

                <div className="form-group">
                    <TextField
                        label="Nouveau mot de passe"
                        type={showPassword ? "text" : "password"}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        error={!!error.password}
                        helperText={error.password}
                        disabled={isLoading}
                        fullWidth
                        InputProps={{
                            startAdornment: <InputAdornment position="start"><Lock /></InputAdornment>,
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={() => setShowPassword(!showPassword)}
                                        edge="end"
                                        disabled={isLoading}
                                    >
                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />

                    {/* Indicateur de force du mot de passe */}
                    {password && (
                        <Box sx={{ width: '100%', mt: 1 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                <Typography variant="caption" color="text.secondary">
                                    Force du mot de passe
                                </Typography>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        fontWeight: 'bold',
                                        color: passwordStrength < 40 ? 'error.main' :
                                               passwordStrength < 60 ? 'warning.main' :
                                               passwordStrength < 100 ? 'info.main' : 'success.main'
                                    }}
                                >
                                    {getPasswordStrengthLabel(passwordStrength)}
                                </Typography>
                            </Box>
                            <LinearProgress
                                variant="determinate"
                                value={passwordStrength}
                                color={getPasswordStrengthColor(passwordStrength)}
                                sx={{
                                    height: 8,
                                    borderRadius: 1,
                                    backgroundColor: 'rgba(0, 0, 0, 0.1)'
                                }}
                            />
                        </Box>
                    )}

                    <TextField
                        label="Confirmer le mot de passe"
                        type={showConfirmPassword ? "text" : "password"}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        error={!!error.confirmPassword}
                        helperText={error.confirmPassword}
                        disabled={isLoading}
                        fullWidth
                        InputProps={{
                            startAdornment: <InputAdornment position="start"><Lock /></InputAdornment>,
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                        edge="end"
                                        disabled={isLoading}
                                    >
                                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                        style={{ marginTop: "1rem" }}
                    />
                </div>

                <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                    className={"login-btn"}
                    onClick={handleSubmit}
                    disabled={isLoading}
                >
                    {isLoading ? "Modification en cours..." : "Modifier le mot de passe"}
                </Button>
            </div>
        </div>
    );
}
